import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, VStack, Text, Box, HStack, Textarea, FormControl, FormLabel,
  Divider, SimpleGrid, Select, Input, Badge, useToast,
  Table, Thead, Tbody, Tr, Th, Td, Stat, StatLabel, StatNumber,
} from '@chakra-ui/react';
import { herramientasApi, Herramienta, HerramientaMovimiento } from '../../../api/serviciosApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  herramienta: Herramienta;
  onCambio?: () => void;
}

const TIPOS = ['Alta', 'Baja', 'Prestamo', 'Devolucion', 'Mantenimiento'];
const TIPO_COLORS: Record<string, string> = {
  Alta: 'green', Baja: 'red', Prestamo: 'orange', Devolucion: 'blue', Mantenimiento: 'purple',
};

const ModalMovimientos: React.FC<Props> = ({ isOpen, onClose, herramienta, onCambio }) => {
  const toast = useToast();
  const [movs, setMovs] = useState<HerramientaMovimiento[]>([]);
  const [disponible, setDisponible] = useState<number>(herramienta.cantidadDisponible);
  const [cargando, setCargando] = useState(false);

  const [tipo, setTipo] = useState('Prestamo');
  const [cantidad, setCantidad] = useState('1');
  const [responsable, setResponsable] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const [lista, herr] = await Promise.all([
        herramientasApi.movimientos(herramienta.id),
        herramientasApi.get(herramienta.id),
      ]);
      setMovs(lista);
      setDisponible(herr.cantidadDisponible);
    } catch {
      setMovs([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (isOpen) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, herramienta.id]);

  const handleRegistrar = async () => {
    const cant = Number(cantidad);
    if (!tipo || !cant || cant <= 0) {
      toast({ title: 'Datos inválidos', description: 'Tipo y cantidad (>0) son requeridos.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      await herramientasApi.addMovimiento(herramienta.id, { tipo, cantidad: cant, responsable, observaciones });
      toast({ title: 'Movimiento registrado', status: 'success', duration: 2500 });
      setCantidad('1'); setResponsable(''); setObservaciones('');
      await cargar();
      onCambio?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo registrar.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
          Movimientos — {herramienta.nombre} ({herramienta.sku})
        </ModalHeader>
        <ModalBody py={5}>
          <VStack spacing={5} align="stretch">
            <HStack spacing={6}>
              <Stat><StatLabel>Cantidad total</StatLabel><StatNumber>{herramienta.cantidad}</StatNumber></Stat>
              <Stat><StatLabel>Disponible</StatLabel><StatNumber color={disponible > 0 ? 'green.600' : 'red.600'}>{disponible}</StatNumber></Stat>
            </HStack>

            <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
              <Text fontSize="sm" fontWeight="bold" color="#5b182b" mb={2}>Registrar movimiento</Text>
              <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Tipo</FormLabel>
                  <Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                    {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Cantidad</FormLabel>
                  <Input type="number" min={1} value={cantidad} onChange={(e) => setCantidad(e.target.value)} />
                </FormControl>
                <FormControl gridColumn={{ md: 'span 2' }}>
                  <FormLabel fontSize="sm">Responsable</FormLabel>
                  <Input value={responsable} onChange={(e) => setResponsable(e.target.value)} />
                </FormControl>
                <FormControl gridColumn={{ md: 'span 4' }}>
                  <FormLabel fontSize="sm">Observaciones</FormLabel>
                  <Textarea rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
                </FormControl>
              </SimpleGrid>
              <HStack justify="flex-end" mt={2}>
                <Button size="sm" bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleRegistrar} isLoading={guardando} loadingText="Guardando...">
                  Registrar
                </Button>
              </HStack>
            </Box>

            <Divider />

            <Box>
              <Text fontSize="sm" fontWeight="bold" color="#5b182b" mb={2}>Historial de movimientos</Text>
              <Box overflowX="auto" borderRadius="md" borderWidth="1px">
                <Table variant="simple" size="sm">
                  <Thead bg="#5b182b">
                    <Tr>
                      <Th color="white" fontSize="xs">Fecha</Th>
                      <Th color="white" fontSize="xs">Tipo</Th>
                      <Th color="white" fontSize="xs">Cant.</Th>
                      <Th color="white" fontSize="xs">Previa → Nueva</Th>
                      <Th color="white" fontSize="xs">Responsable</Th>
                      <Th color="white" fontSize="xs">Observaciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {cargando ? (
                      <Tr><Td colSpan={6} textAlign="center" py={6}><Text color="gray.500">Cargando...</Text></Td></Tr>
                    ) : movs.length === 0 ? (
                      <Tr><Td colSpan={6} textAlign="center" py={6}><Text color="gray.400">Sin movimientos</Text></Td></Tr>
                    ) : (
                      movs.map((m, idx) => (
                        <Tr key={m.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'}>
                          <Td fontSize="xs">{m.fecha ? new Date(m.fecha).toLocaleString('es-MX') : '—'}</Td>
                          <Td><Badge colorScheme={TIPO_COLORS[m.tipo] || 'gray'} fontSize="2xs">{m.tipo}</Badge></Td>
                          <Td fontSize="sm">{m.cantidad}</Td>
                          <Td fontSize="sm">{m.existenciaPrevia ?? '—'} → {m.existenciaNueva ?? '—'}</Td>
                          <Td fontSize="sm">{m.responsable || '—'}</Td>
                          <Td fontSize="xs" maxW="200px"><Text noOfLines={2}>{m.observaciones || '—'}</Text></Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter><Button onClick={onClose}>Cerrar</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalMovimientos;
